import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useFetch from '../../hooks/useFetch';
import { buildEndpoint } from '../../app/services/EndPoints';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import InputField from '../../components/InputField';
import logoMonere from '../../assets/logo-monere-fondo-oscuro.png';
import PDFSignatureEditor from '../../components/PDFSignatureEditor/PDFSignatureEditor';

interface InvitationData {
    invitation: {
        id: number;
        signer_name: string;
        signer_email: string;
        status: string;
        can_sign: boolean;
        expires_at: string | null;
    };
    contract: {
        id: number;
        reference_code: string;
        contract_type: string;
        document_url: string | null;
        signatures_required: number;
        signatures_completed: number;
    };
    sender: {
        name: string;
        email: string;
    };
    needs_account: boolean;
    custom_message: string | null;
}

const SignContract: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const history = useHistory();
    const { doGet, doPost } = useFetch();
    
    const [loading, setLoading] = useState(true);
    const [signing, setSigning] = useState(false);
    const [data, setData] = useState<InvitationData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [signed, setSigned] = useState(false);
    
    // Para el editor de firma
    const [showPdfEditor, setShowPdfEditor] = useState(false);
    
    // Para crear cuenta
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        loadInvitation();
    }, [token]);

    const loadInvitation = () => {
        setLoading(true);
        doGet({
            url: buildEndpoint(`app/contract-drafting/sign/${token}`),
            setFetching: setLoading,
            onSuccess: (res) => {
                setData(res.data);
                setError(null);
            },
            onError: (res) => {
                setError(res.message || 'Error al cargar la invitación');
            }
        });
    };

    const handleOpenPdfEditor = () => {
        // Validar contraseña si necesita crear cuenta antes de abrir el editor
        if (data?.needs_account) {
            if (!password) {
                toast.error('Por favor, ingrese una contraseña para crear su cuenta');
                return;
            }
            if (password.length < 6) {
                toast.error('La contraseña debe tener al menos 6 caracteres');
                return;
            }
            if (password !== confirmPassword) {
                toast.error('Las contraseñas no coinciden');
                return;
            }
        }
        setShowPdfEditor(true);
    };

    const handleSign = (signedPdfBlob: Blob, signatures: any[]) => {
        if (signatures.length === 0) {
            toast.error('Por favor, coloque al menos una firma en el documento');
            return;
        }

        // Obtener la primera firma como base64
        const signatureData = signatures.find(s => s.type === 'signature')?.signatureDataUrl || '';
        
        if (!signatureData) {
            toast.error('Por favor, agregue su firma al documento');
            return;
        }

        setSigning(true);
        setShowPdfEditor(false);

        // NUEVO FLUJO: Enviar solo los placements (coordenadas + imagen)
        // El backend agrega las imágenes al PDF de forma incremental
        // Esto preserva las firmas digitales anteriores
        
        // Preparar placements con la información necesaria
        const signaturePlacements = signatures.map(sig => ({
            x: sig.x,
            y: sig.y,
            width: sig.width,
            height: sig.height,
            page: sig.page,
            type: sig.type,
            signatureDataUrl: sig.signatureDataUrl || signatureData,
            textValue: sig.type === 'name' ? data?.invitation.signer_name : 
                       sig.type === 'date' ? new Date().toLocaleDateString('es-CL') : ''
        }));

        // Crear FormData
        const formData = new FormData();
        formData.append('signature_data', signatureData);
        formData.append('create_account', String(data?.needs_account || false));
        if (data?.needs_account && password) {
            formData.append('password', password);
        }
        
        // Enviar placements como JSON (nuevo flujo incremental)
        formData.append('signature_placements', JSON.stringify(signaturePlacements));

        doPost({
            url: buildEndpoint(`app/contract-drafting/sign/${token}/complete`),
            body: formData,
            requestType: 'multipart',
            setFetching: setSigning,
            onSuccess: (res) => {
                toast.success(res.message || '¡Documento firmado exitosamente!');
                setSigned(true);
            },
            onError: (res) => {
                toast.error(res.message || 'Error al firmar el documento');
            }
        });
    };

    const handleCancel = () => {
        setShowPdfEditor(false);
    };

    // Construir la URL del PDF usando el endpoint proxy del backend para evitar CORS
    const getPdfUrl = () => {
        if (!token) return '';
        // Usar el endpoint del backend como proxy para evitar problemas de CORS con S3
        return buildEndpoint(`app/contract-drafting/sign/${token}/pdf`);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f5f7fa' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-3">Cargando invitación...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f5f7fa' }}>
                <div className="card shadow" style={{ maxWidth: '500px', width: '100%', margin: '20px' }}>
                    <div className="card-body text-center py-5">
                        <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                            style={{
                                width: '80px',
                                height: '80px',
                                background: '#dc3545',
                                color: 'white',
                                fontSize: '36px'
                            }}
                        >
                            <i className="fas fa-times"></i>
                        </div>
                        <h4 className="mb-3">No se puede acceder</h4>
                        <p className="text-muted">{error}</p>
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            title="Ir al inicio"
                            onClick={() => history.push('/login')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (signed) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f5f7fa' }}>
                <div className="card shadow" style={{ maxWidth: '500px', width: '100%', margin: '20px' }}>
                    <div className="card-body text-center py-5">
                        <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                            style={{
                                width: '100px',
                                height: '100px',
                                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                color: 'white',
                                fontSize: '48px'
                            }}
                        >
                            <i className="fas fa-check"></i>
                        </div>
                        <h3 className="mb-3">¡Firma Completada!</h3>
                        <p className="text-muted mb-4">
                            Has firmado el documento exitosamente.
                            {data?.needs_account && ' Se ha creado tu cuenta en Monere, recibirás un email con los detalles.'}
                        </p>
                        <div className="alert alert-info">
                            <small>
                                <strong>Documento:</strong> {data?.contract.contract_type}<br />
                                <strong>Referencia:</strong> {data?.contract.reference_code}
                            </small>
                        </div>
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            title="Ir al inicio"
                            onClick={() => history.push('/login')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #001849 0%, #003380 100%)', color: 'white', padding: '20px 0' }}>
                <div className="container">
                    <img src={logoMonere} alt="Monere" height="44" className="d-block mx-auto mb-2" />
                    <h2 className="mb-0 text-center text-white">Firma de Documento</h2>
                </div>
            </div>

            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        {/* Info del documento */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-body">
                                <h5 className="card-title mb-3">
                                    <i className="fas fa-file-contract me-2 text-primary"></i>
                                    Información del Documento
                                </h5>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Tipo de Documento</small>
                                        <p className="mb-0 fw-bold">{data?.contract.contract_type}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Referencia</small>
                                        <p className="mb-0">{data?.contract.reference_code}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Enviado por</small>
                                        <p className="mb-0">{data?.sender.name}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <small className="text-muted d-block">Estado de Firmas</small>
                                        <p className="mb-0">
                                            <span className="badge bg-primary">
                                                {data?.contract.signatures_completed} de {data?.contract.signatures_required} firmas
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                {data?.custom_message && (
                                    <div className="alert alert-info mt-3 mb-0">
                                        <i className="fas fa-comment me-2"></i>
                                        <strong>Mensaje del remitente:</strong>
                                        <p className="mb-0 mt-1">{data.custom_message}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Crear cuenta (si es necesario) */}
                        {data?.needs_account && (
                            <div className="card shadow-sm mb-4">
                                <div className="card-header" style={{ backgroundColor: '#001849', color: 'white' }}>
                                    <h6 className="mb-0 text-white">
                                        <i className="fas fa-user-plus me-2"></i>
                                        Crear tu Cuenta
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted small mb-3">
                                        Al firmar crearemos tu cuenta en Monere con el email 
                                        <strong> {data.invitation.signer_email}</strong>. Elige una contraseña:
                                        con ella podrás volver a entrar cuando quieras y descargar el documento
                                        firmado por todas las partes, incluso cuando tu enlace de firma ya haya caducado.
                                    </p>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <InputField
                                                label="Contraseña"
                                                type="password"
                                                value={password}
                                                onChange={setPassword}
                                                placeholder="Mínimo 6 caracteres"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <InputField
                                                label="Confirmar Contraseña"
                                                type="password"
                                                value={confirmPassword}
                                                onChange={setConfirmPassword}
                                                placeholder="Repita la contraseña"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Card de acción para firmar */}
                        <div className="card shadow-sm mb-4 border-0" style={{ background: 'linear-gradient(135deg, #001849 0%, #1a3a6e 100%)' }}>
                            <div className="card-body py-4 text-center text-white">
                                <div 
                                    className="d-inline-flex align-items-center justify-content-center mb-3"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(255,255,255,0.15)'
                                    }}
                                >
                                    <i className="fas fa-signature" style={{ fontSize: '24px' }}></i>
                                </div>
                                <h5 className="mb-2 text-white">¿Listo para firmar?</h5>
                                <p className="mb-4 opacity-75" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                    Abrirás el documento donde podrás ver el contenido completo y agregar tu firma digital.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-light btn-lg px-5"
                                    onClick={handleOpenPdfEditor}
                                    disabled={signing || !data?.invitation.can_sign}
                                    style={{ fontWeight: 600, color: '#001849' }}
                                >
                                    {signing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-file-signature me-2"></i>
                                            Abrir y Firmar Documento
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Instrucciones */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-body">
                                <h6 className="mb-3">
                                    <i className="fas fa-info-circle me-2 text-primary"></i>
                                    ¿Cómo funciona?
                                </h6>
                                <ol className="mb-0 ps-3" style={{ lineHeight: '2' }}>
                                    <li>Haz clic en <strong>&quot;Abrir y Firmar Documento&quot;</strong></li>
                                    <li>Se abrirá el documento PDF para que lo revises</li>
                                    <li>Crea tu firma (dibujando, escribiendo o subiendo una imagen)</li>
                                    <li>Haz clic en el documento donde quieres colocar tu firma</li>
                                    <li>Puedes mover y redimensionar tu firma</li>
                                    <li>Presiona <strong>&quot;Firmar Documento&quot;</strong> para completar</li>
                                </ol>
                            </div>
                        </div>

                        {/* Disclaimer */}
                        <div className="text-center">
                            <small className="text-muted">
                                <i className="fas fa-shield-alt me-1"></i>
                                Al firmar este documento, usted acepta que su firma electrónica tiene 
                                la misma validez legal que una firma manuscrita según la Ley 19.799 sobre 
                                Documentos Electrónicos, Firma Electrónica y Servicios de Certificación de Chile.
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Editor de PDF para firma */}
            {data && (
                <PDFSignatureEditor
                    pdfUrl={getPdfUrl()}
                    documentTitle={`${data.contract.contract_type} - ${data.contract.reference_code}`}
                    userName={data.invitation.signer_name}
                    onSign={handleSign}
                    onCancel={handleCancel}
                    isVisible={showPdfEditor}
                />
            )}
        </div>
    );
};

export default SignContract;
